import type { DateTime } from "luxon";
import type { enJSONKeys } from "../../../../../i18n/en/types";
import type { TranslationKeys } from "../../../../../i18n/types";
import { SchedulePushSource, type SchedulePushWindow } from "./schedulePushTypes";

/**
 * Each vendor's name as the panel prints it — the one place the panel learns it. A proper noun, the same in every
 * language, so it is a value here rather than a catalog entry; the shared strings take it as `{{vendor}}`. Keyed by
 * every source, so a new vendor doesn't type-check until it has a name.
 *
 * The config GET also sends `vendorName`, but the panel never prints that one: the section's title, its loading line
 * and its load-failed banner name the vendor before (or without) any answer, and a name read from the answer would
 * swap in under them the moment it lands. One name, known before the first request, is the only one shown.
 */
export const SCHEDULE_PUSH_VENDOR_NAMES: Readonly<Record<SchedulePushSource, string>> = {
	[SchedulePushSource.HostedTime]: "HostedTime",
	[SchedulePushSource.AttendanceOnDemand]: "Attendance on Demand",
};

/**
 * Every string of the panel lives under this namespace in the three catalogs. A string every vendor shares names the
 * vendor as `{{vendor}}`; one about a single vendor's own concept lives under that vendor's source (`hostdime.*`).
 */
const SCHEDULE_PUSH_NAMESPACE = "department.config.integrations.schedulePush";

type PanelCatalogKey = Extract<enJSONKeys, `${typeof SCHEDULE_PUSH_NAMESPACE}.${string}`>;
type PanelSuffix<Key> = Key extends `${typeof SCHEDULE_PUSH_NAMESPACE}.${infer Suffix}` ? Suffix : never;
/** A plural key's base (`status.notMapped` for `status.notMapped_one`): what `t(key, { count })` is called with. */
type PluralBase<Suffix> = Suffix extends `${infer Base}_${"zero" | "one" | "two" | "many" | "other"}` ? Base : never;

/** The part of a panel key after the namespace, checked against the English catalog (es/he parity is a spec). */
export type SchedulePushKeySuffix = PanelSuffix<PanelCatalogKey> | PluralBase<PanelSuffix<PanelCatalogKey>>;
export type SchedulePushKey = `${typeof SCHEDULE_PUSH_NAMESPACE}.${SchedulePushKeySuffix}`;

/** A panel string's full key. A typo in the suffix fails the type check instead of rendering the raw key. */
export const schedulePushKey = (suffix: SchedulePushKeySuffix): SchedulePushKey => `${SCHEDULE_PUSH_NAMESPACE}.${suffix}` as const;

/** The backend's generic preview failure, shown for a failed row that somehow carries no key of its own. */
export const PREVIEW_FAILED_ERROR_KEY: TranslationKeys = "schedule.push.preview.failed";

/**
 * The picker's "no unit" option. Unit names are never empty (the backend refuses a blank one), so the empty string
 * cannot collide with a real unit — the same sentinel pattern as `HOLIDAY_NONE`.
 */
export const NO_UNIT = "";

/**
 * `updated_by` of a target the one-off migration from the old code constants created, rather than a person
 * (it wrote `backfill:schedule-push`; the script is gone, its rows stay). Matched by prefix and kept
 * here rather than sent as a flag because it only decides wording: a name that stops matching would print the raw
 * value — wrong-looking, never wrong — and no API change is worth a label.
 */
export const AUTOMATIC_SETUP_PREFIX = "backfill:";

/**
 * Changes one hourly send makes per department (`MAX_MUTATIONS_PER_RUN` in `backend/services/schedule_push/engine.py`).
 * The preview is not capped, so a first load bigger than this is said to spread over several hours.
 */
export const SEND_CHANGES_PER_HOUR = 400;

/**
 * Days either side of today a send compares (`WINDOW_DAYS_BACK` / `WINDOW_DAYS_FORWARD` in
 * `backend/services/schedule_push/window.py`, both 30). Said in the stop confirmation: what a restart catches up on.
 */
export const SEND_WINDOW_DAYS_EACH_SIDE = 30;

/**
 * How far ahead Attendance on Demand's forward-only push reaches: tomorrow to this many days out
 * (`FORWARD_WINDOW_DAYS` in `backend/services/schedule_push/window.py`). Said in the stop confirmation, where no
 * window is on screen to read it from.
 */
export const FORWARD_WINDOW_DAYS = 28;

/** The push window as the panel prints it ("Sep 17" – "Nov 16"); computed once and handed to every part that says it. */
export type Period = { start: string; end: string };

const SHORT_DAY = { month: "short", day: "numeric" } as const;

/**
 * The window's dates are calendar dates, read at midnight in the department's zone by the response converter; they are
 * printed as they are, never shifted into another zone (which could move them by a day).
 */
export const formatPeriod = (window: SchedulePushWindow): Period => ({ start: formatCalendarDay(window.start), end: formatCalendarDay(window.end) });

/** One calendar date of the window (or one derived from it) as the panel prints it: "Sep 17". */
export const formatCalendarDay = (date: DateTime): string => date.toLocaleString(SHORT_DAY);

/**
 * A moment (a "last changed" or "started" line) in the department's own zone. The response converter already reads
 * timestamps in it, so this only makes that explicit — a screen that rendered before the department's zone was set
 * would otherwise print the browser's.
 */
export const formatMomentDay = (moment: DateTime, timezone: string): string => moment.setZone(timezone).toLocaleString(SHORT_DAY);
export const formatMomentTime = (moment: DateTime, timezone: string): string => moment.setZone(timezone).toLocaleString({ hour: "numeric", minute: "2-digit" });

/** Layout measures of the panel: named so the table's columns, the prose measure and the action row agree. */
export const PANEL_LAYOUT = {
	/** The location column's floor, so a long location name wraps instead of crushing the column to a letter. */
	LOCATION_COLUMN_MIN_PX: 150,
	/** The shift count: wide enough for four digits and its header. */
	SHIFTS_COLUMN_PX: 80,
	/** The unit picker: a typical unit label plus the chevron. */
	UNIT_COLUMN_PX: 220,
	/** On a phone the unit column takes what the location leaves, down to this — the picker's value plus its chevron. */
	UNIT_COLUMN_MIN_PX: 130,
	/** "What happens": a status chip, and a suggestion chip that wraps under it. */
	STATUS_COLUMN_MIN_PX: 200,
	/** Where the search box stops shrinking and the filter drops to its own line. */
	SEARCH_MIN_PX: 200,
	SEARCH_MAX_PX: 320,
	/** Where the reasons under the card's buttons stop sharing a line with them. */
	REASONS_MIN_PX: 260,
} as const;

/**
 * A status chip that wraps onto a second line instead of cutting its words off — for a chip whose words are the point
 * (an instruction, a unit name), where `MetaChip`'s ellipsis would hide the part to act on.
 */
export const WRAPPING_CHIP_SX = { whiteSpace: "normal" } as const;
